import React, {useState, useEffect, useMemo, useRef} from 'react';
import {Search, Shield, ChevronDown, ChevronRight, Filter, RotateCcw} from 'react-feather';
import {useApi} from "../hooks/useApi";
import {usePageSize} from "../hooks/usePageSize";
import {useHasLicense} from "../contexts/LicenseContext";
import {useAuth} from "../contexts/AuthContext";
import {useRBAC} from "../hooks/useRBAC";

import Navigation from '../components/Navigation';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorBoundary from "../components/ErrorBoundary";
import ActivityLogTab from '../components/ActivityLogTab';
import EmailLogTab from '../components/EmailLogTab';
import Pagination from '../components/Pagination';


const RiskRegister = () => {
    const [acceptedRisks, setAcceptedRisks] = useState([]);
    const [filteredRisks, setFilteredRisks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [allDeals, setAllDeals] = useState([]);
    const [allBusinessUnits, setAllBusinessUnits] = useState([]);
    const [allDealApplications, setAllDealApplications] = useState([]);
    const [loadingDealApps, setLoadingDealApps] = useState(false);
    const [filterSeverity, setFilterSeverity] = useState('all');
    const [filterApplication, setFilterApplication] = useState('all');
    const [filterVendor, setFilterVendor] = useState('all');
    const [filterDeal, setFilterDeal] = useState('all');
    const [filterBusinessUnit, setFilterBusinessUnit] = useState('all');
    const [filterBUApplication, setFilterBUApplication] = useState('all');
    const [filterFramework, setFilterFramework] = useState('all');
    const [filterAllApplication, setFilterAllApplication] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('acceptedAt');
    const [sortOrder, setSortOrder] = useState('desc');
    const [filterControlSource, setFilterControlSource] = useState('all');
    const [filterRiskCategory, setFilterRiskCategory] = useState('all');
    const [filterAssignedTo, setFilterAssignedTo] = useState('all');
    const [users, setUsers] = useState([]);
    const [filtersExpanded, setFiltersExpanded] = useState(false);
    const [page, setPage] = useState(1);
    const [cardTabs, setCardTabs] = useState({});
    const [pageSize, setPageSize] = usePageSize('riskRegister');
    const listTopRef = useRef(null);
    const apiCall = useApi();
    const hasLicense = useHasLicense();
    const { user } = useAuth();
    const { hasMinimumRoleAtOrAbove, canManageOrganizationRisks, canUpdateRiskViaAssignment } = useRBAC();

    // All possible filter types
    const allFilterTypes = ['Deals', 'BusinessUnits', 'Enterprise', 'TPRM', 'Ransomware', 'DarkWeb', 'AI', 'GRC'];

    // Filter to only licensed types
    const licensedFilterTypes = useMemo(() => {
        return allFilterTypes.filter(type => hasLicense(type));
    }, [hasLicense]);

    // Initialize filterTypes state with only licensed types enabled
    const [filterTypes, setFilterTypes] = useState(() => {
        const initial = {};
        allFilterTypes.forEach(type => {
            initial[type] = true;
        });
        return initial;
    });

    const toggleTypeFilter = (type) => {
        setFilterTypes(prev => ({...prev, [type]: !prev[type]}));
    };

    const assignedUserIdOf = (risk) =>
        risk.remediationAssignTo ? parseInt(risk.remediationAssignTo) : (risk.assignedToUserId ?? null);

    const resetAllFilters = () => {
        setFilterTypes(() => {
            const initial = {};
            allFilterTypes.forEach(type => { initial[type] = true; });
            return initial;
        });
        setFilterSeverity('all');
        setFilterApplication('all');
        setFilterVendor('all');
        setFilterDeal('all');
        setFilterBusinessUnit('all');
        setFilterBUApplication('all');
        setFilterFramework('all');
        setFilterAllApplication('all');
        setFilterControlSource('all');
        setFilterRiskCategory('all');
        setFilterAssignedTo('all');
        setSearchTerm('');
    };

    // Unique deals from risks (show name, filter by ID)
    const uniqueDeals = acceptedRisks
        .filter(r => r.dealId && r.dealName)
        .map(r => ({dealId: r.dealId, dealName: r.dealName}))
        .filter((deal, index, self) => index === self.findIndex(d => d.dealId === deal.dealId))
        .sort((a, b) => (a.dealName || '').localeCompare(b.dealName || ''));

    // Unique applications - only show those matching selected deal
    const uniqueApplications = acceptedRisks
        .filter(r => filterDeal === 'all' || String(r.dealId) === String(filterDeal))
        .filter(r => r.applicationName)
        .map(r => r.applicationName)
        .filter((name, index, self) => self.indexOf(name) === index)
        .sort();

    // Unique BU applications - only show those matching selected business unit
    const uniqueBUApplications = acceptedRisks
        .filter(r => r.riskType === 'BusinessUnits')
        .filter(r => filterBusinessUnit === 'all' || String(r.businessUnitId) === String(filterBusinessUnit))
        .filter(r => r.applicationName)
        .map(r => r.applicationName)
        .filter((name, index, self) => self.indexOf(name) === index)
        .sort();

    // Unique vendors from risks
    const uniqueVendors = acceptedRisks
        .filter(r => r.vendorName)
        .map(r => r.vendorName)
        .filter((name, index, self) => self.indexOf(name) === index)
        .sort();

    const uniqueFrameworks = useMemo(() => {
        const frameworkSet = new Set();
        acceptedRisks.forEach(r => {
            if (r.affectedFrameworks) {
                r.affectedFrameworks.forEach(f => frameworkSet.add(f));
            }
        });
        return [...frameworkSet].sort();
    }, [acceptedRisks]);

    const uniqueAllApplications = acceptedRisks
        .filter(r => r.applicationName)
        .map(r => r.applicationName)
        .filter((name, index, self) => self.indexOf(name) === index)
        .sort();

    const uniqueControlSources = useMemo(() => {
        return [...new Set(acceptedRisks.filter(r => r.controlSource).map(r => r.controlSource))].sort();
    }, [acceptedRisks]);

    const uniqueRiskCategories = useMemo(() => {
        let source = acceptedRisks.filter(r => r.riskCategory);
        if (filterControlSource !== 'all') {
            source = source.filter(r => r.controlSource === filterControlSource);
        }
        return [...new Set(source.map(r => r.riskCategory))].sort();
    }, [acceptedRisks, filterControlSource]);

    useEffect(() => {
        fetchAcceptedRisks();
        fetchAllDeals();
        fetchAllBusinessUnits();
        fetchUsers();
    }, []);

    useEffect(() => {
        applyFilters();
    }, [acceptedRisks, filterSeverity, filterApplication, filterVendor, filterDeal, filterBusinessUnit, filterBUApplication, filterFramework, filterAllApplication, filterControlSource, filterRiskCategory, filterAssignedTo, filterTypes, searchTerm, sortBy, sortOrder]);

    // Reset application filter when deal changes
    useEffect(() => {
        setFilterApplication('all');
    }, [filterDeal]);

    // Fetch all applications for selected deal from API
    useEffect(() => {
        setAllDealApplications([]);
        if (filterDeal === 'all') return;
        const fetchApps = async () => {
            setLoadingDealApps(true);
            try {
                const apps = await apiCall("GET", `/applications/deal/${filterDeal}`);
                setAllDealApplications(
                    (apps || [])
                        .filter(a => a.applicationName)
                        .sort((a, b) => (a.applicationName || '').localeCompare(b.applicationName || ''))
                );
            } catch (err) {} finally {
                setLoadingDealApps(false);
            }
        };
        fetchApps();
    }, [filterDeal]);

    // Reset BU application filter when business unit changes
    useEffect(() => {
        setFilterBUApplication('all');
    }, [filterBusinessUnit]);

    // Set vendor filter when deal application changes
    useEffect(() => {
        if (filterApplication === 'all') {
            if (filterBUApplication === 'all') {
                setFilterVendor('all');
            }
        } else {
            // Find the vendor associated with this application
            const appRisk = acceptedRisks.find(r => r.applicationName === filterApplication);
            if (appRisk?.vendorName) {
                setFilterVendor(appRisk.vendorName);
            } else {
                setFilterVendor('all');
            }
        }
    }, [filterApplication, acceptedRisks]);

    // Set vendor filter when BU application changes
    useEffect(() => {
        if (filterBUApplication === 'all') {
            if (filterApplication === 'all') {
                setFilterVendor('all');
            }
        } else {
            const buRisk = acceptedRisks.find(r => r.riskType === 'BusinessUnits' && r.applicationName === filterBUApplication);
            if (buRisk?.vendorName) {
                setFilterVendor(buRisk.vendorName);
            } else {
                setFilterVendor('all');
            }
        }
    }, [filterBUApplication, acceptedRisks]);

    const fetchAcceptedRisks = async () => {
        try {
            const org = JSON.parse(localStorage.getItem("selectedOrganization"));
            if (!org?.organizationId) {
                return;
            }

            // Fetch all risk types from consolidated endpoint
            const allRisks = await apiCall("GET", "/allRisks/details?status=Accepted");

            // Resolve acceptedBy display name from backend user fields
            const combined = (allRisks || []).map(r => ({
                ...r,
                acceptedBy: r.acceptedByUserName || r.acceptedByEmail || r.acceptedBy
            }));

            setAcceptedRisks(combined);
        } catch (error) {} finally {
            setLoading(false);
        }
    };

    const fetchAllDeals = async () => {
        try {
            const deals = await apiCall("GET", "/Deals");
            // API returns DealID (serialised as dealID), normalise to a consistent shape
            const normalised = (deals || []).map(d => ({
                dealId: d.dealID ?? d.dealId ?? d.DealID ?? d.DealId,
                dealName: d.dealName ?? d.DealName,
            }));
            setAllDeals(
                normalised
                    .filter(d => d.dealId && d.dealName)
                    .filter((d, i, self) => i === self.findIndex(x => x.dealId === d.dealId))
                    .sort((a, b) => (a.dealName || '').localeCompare(b.dealName || ''))
            );
        } catch (err) {}
    };

    const fetchUsers = async () => {
        try {
            const response = await apiCall('GET', '/users');
            setUsers(response || []);
        } catch (error) {}
    };

    const fetchAllBusinessUnits = async () => {
        try {
            const bus = await apiCall("GET", "/businessUnits/all");
            setAllBusinessUnits(
                (bus || [])
                    .map(b => ({
                        businessUnitId: b.businessUnitId ?? b.BusinessUnitId,
                        businessUnitName: b.businessUnitName ?? b.BusinessUnitName,
                    }))
                    .filter(b => b.businessUnitId && b.businessUnitName)
                    .filter((b, i, self) => i === self.findIndex(x => x.businessUnitId === b.businessUnitId))
                    .sort((a, b) => (a.businessUnitName || '').localeCompare(b.businessUnitName || ''))
            );
        } catch (err) {}
    };

    const applyFilters = () => {
        let filtered = [...acceptedRisks];

        // Filter by risk type — match by primary riskType OR any cross-listing tag (e.g. a vendor-
        // associated "External" risk tagged "TPRM"). Tag matches are gated to licensed/visible buckets,
        // mirroring AllRisks.js so a hidden (default-on) checkbox can't force-include risks.
        filtered = filtered.filter(r =>
            filterTypes[r.riskType] ||
            (r.tags || []).some(t => licensedFilterTypes.includes(t) && filterTypes[t]));

        if (filterSeverity !== 'all') {
            filtered = filtered.filter(r => r.riskSeverity?.toUpperCase() === filterSeverity.toUpperCase());
        }
        if (filterApplication !== 'all') {
            filtered = filtered.filter(r => r.applicationName === filterApplication);
        }
        if (filterVendor !== 'all') {
            filtered = filtered.filter(r => r.vendorName === filterVendor);
        }
        if (filterDeal !== 'all') {
            filtered = filtered.filter(r => String(r.dealId) === String(filterDeal));
        }
        if (filterBusinessUnit !== 'all') {
            filtered = filtered.filter(r => String(r.businessUnitId) === String(filterBusinessUnit));
        }
        if (filterBUApplication !== 'all') {
            filtered = filtered.filter(r => r.riskType === 'BusinessUnits' && r.applicationName === filterBUApplication);
        }
        if (filterFramework !== 'all') {
            filtered = filtered.filter(r =>
                r.affectedFrameworks && r.affectedFrameworks.includes(filterFramework)
            );
        }
        if (filterAllApplication !== 'all') {
            filtered = filtered.filter(r => r.applicationName === filterAllApplication);
        }
        if (filterControlSource !== 'all') {
            filtered = filtered.filter(r => r.controlSource === filterControlSource);
        }
        if (filterRiskCategory !== 'all') {
            filtered = filtered.filter(r => r.riskCategory === filterRiskCategory);
        }
        if (filterAssignedTo === 'unassigned') {
            filtered = filtered.filter(r => !assignedUserIdOf(r));
        } else if (filterAssignedTo === 'mine') {
            filtered = filtered.filter(r => String(assignedUserIdOf(r)) === String(user?.id));
        } else if (filterAssignedTo !== 'all') {
            filtered = filtered.filter(r => String(assignedUserIdOf(r)) === String(filterAssignedTo));
        }
        if (searchTerm) {
            filtered = filtered.filter(r => r.riskTitle.toLowerCase().includes(searchTerm.toLowerCase()) || (r.riskDescription && r.riskDescription.toLowerCase().includes(searchTerm.toLowerCase())) || (r.applicationName && r.applicationName.toLowerCase().includes(searchTerm.toLowerCase())) || (r.dealName && r.dealName.toLowerCase().includes(searchTerm.toLowerCase())) || (r.businessUnitName && r.businessUnitName.toLowerCase().includes(searchTerm.toLowerCase())) || (r.userReason && r.userReason.toLowerCase().includes(searchTerm.toLowerCase())));
        }

        filtered.sort((a, b) => {
            let aVal = a[sortBy];
            let bVal = b[sortBy];

            if (sortBy === 'acceptedAt') {
                aVal = aVal ? new Date(aVal) : new Date('1970-01-01');
                bVal = bVal ? new Date(bVal) : new Date('1970-01-01');
            }

            if (sortOrder === 'asc') return aVal > bVal ? 1 : -1;
            return aVal < bVal ? 1 : -1;
        });

        setFilteredRisks(filtered);
        setPage(1);
    };

    const totalPages = Math.max(1, Math.ceil(filteredRisks.length / pageSize));
    const pagedRisks = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filteredRisks.slice(start, start + pageSize);
    }, [filteredRisks, page, pageSize]);

    const getTypeColor = (type) => {
        switch (type) {
            case 'Deals':
                return 'text-blue-600 bg-blue-100 border-blue-300';
            case 'DarkWeb':
                return 'text-purple-600 bg-purple-100 border-purple-300';
            case 'Ransomware':
                return 'text-red-600 bg-red-100 border-red-300';
            case 'Enterprise':
                return 'text-green-600 bg-green-100 border-green-300';
            case 'TPRM':
                return 'text-teal-600 bg-teal-100 border-teal-300';
            case 'AI':
                return 'text-amber-600 bg-amber-100 border-amber-300';
            case 'BusinessUnits':
                return 'text-indigo-600 bg-indigo-100 border-indigo-300';
            case 'GRC':
                return 'text-rose-600 bg-rose-100 border-rose-300';
            default:
                return 'text-gray-600 bg-gray-100 border-gray-300';
        }
    };

    const getSeverityColor = (severity) => {
        switch ((severity || '').toUpperCase()) {
            case 'CRITICAL':
                return 'bg-red-100 text-red-700 border-red-300';
            case 'HIGH':
                return 'bg-orange-100 text-orange-800 border-orange-300';
            case 'MEDIUM':
                return 'bg-yellow-100 text-yellow-800 border-yellow-300';
            case 'LOW':
                return 'bg-green-100 text-green-800 border-green-300';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-300';
        }
    };

    const formatSeverityLabel = (severity) => {
        if (!severity) return '';
        return severity.charAt(0).toUpperCase() + severity.slice(1).toLowerCase();
    };

    const getTypeLabel = (type) => {
        switch (type) {
            case 'Deals':
                return 'Deals';
            case 'DarkWeb':
                return 'Dark Web';
            case 'AI':
                return 'AI';
            case 'BusinessUnits':
                return 'Business Units';
            case 'GRC':
                return 'GRC';
            default:
                return type || 'Unknown';
        }
    };

    const getAssessmentDescription = (risk) => {
        const typeLabel = {
            'Deals': '',
            'Enterprise': 'Enterprise',
            'Ransomware': 'Ransomware',
            'DarkWeb': 'DarkWeb',
            'TPRM': 'TPRM',
            'AI': 'AI',
            'BusinessUnits': '',
            'GRC': 'GRC',
        }[risk.riskType] || '';

        if (risk.dealName) {
            return `Deal ${risk.dealName}${typeLabel ? ' ' + typeLabel : ''} Assessment`;
        }
        if (risk.businessUnitName) {
            return `Business Unit ${risk.businessUnitName}${typeLabel ? ' ' + typeLabel : ''} Assessment`;
        }
        return `${typeLabel} Organization Assessment`.trim();
    };

    const canReopenRisk = (risk) => {
        const org = JSON.parse(localStorage.getItem("selectedOrganization"));
        if (!org?.organizationId) return false;
        const orgId = parseInt(org.organizationId);

        // Risk Manager+ can reopen any risk
        if (canManageOrganizationRisks(orgId)) return true;

        // Contributor can reopen if assigned to them
        if (hasMinimumRoleAtOrAbove('CONTRIBUTOR', 'ORG', orgId)) {
            return risk.assignedToUserId && risk.assignedToUserId === user?.id;
        }

        // SELF-scoped users can reopen risks assigned to them
        if (canUpdateRiskViaAssignment(risk.assignedToUserId)) return true;

        return false;
    };

    const getReopenEndpoint = (risk) => {
        // All risks live in UnifiedRisks; reopen via the unified controller (keyed by the GUID).
        return `/unifiedrisks/${risk.id}/reopen`;
    };

    const handleReopenRisk = async (risk) => {
        if (!window.confirm('Are you sure you want to re-open this risk?')) return;

        try {
            const endpoint = getReopenEndpoint(risk);
            await apiCall('PUT', endpoint);
            // Remove the reopened risk from the list
            setAcceptedRisks(prev => prev.filter(r => !(r.id === risk.id && r.riskType === risk.riskType)));
        } catch (error) {
            alert('Failed to reopen risk. Please try again.');
        }
    };

    if (loading) return <LoadingSpinner/>;

    return (<ErrorBoundary>
            <div className="min-h-screen bg-theme-primary">
                <Navigation/>

                <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
                    <div className="mb-8">
                        <h1 className="text-3xl font-bold text-content-primary">Organization Risk Register</h1>
                        <p className="mt-2 text-gray-600">
                            Centralized register of all accepted risks across the organization
                        </p>
                    </div>

                    {/* Filters & Search */}
                    <div className="bg-white rounded-lg shadow mb-4">
                        <button
                            onClick={() => setFiltersExpanded(!filtersExpanded)}
                            className="w-full px-4 py-2 flex items-center justify-between text-left hover:bg-gray-50 rounded-lg transition-colors"
                        >
                            <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                <Filter className="h-4 w-4"/> Filters
                            </span>
                            {filtersExpanded ? <ChevronDown className="h-4 w-4 text-gray-500"/> : <ChevronRight className="h-4 w-4 text-gray-500"/>}
                        </button>
                        {filtersExpanded && (<div className="px-4 py-3 border-t border-gray-200">
                        {/* Type Checkboxes */}
                        <div className="flex items-center space-x-6 mb-3">
                            <span className="text-sm font-medium text-gray-700">Type:</span>
                            {licensedFilterTypes.map(type => (
                                <label key={type} className="flex items-center space-x-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={filterTypes[type]}
                                        onChange={() => toggleTypeFilter(type)}
                                        className="h-4 w-4 text-blue-600 rounded border-gray-300"
                                    />
                                    <span className="text-sm text-gray-700">{getTypeLabel(type)}</span>
                                </label>))}
                        </div>
                        {/* Row 2: Severity */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                            {/* Severity Filter */}
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Risk Severity</label>
                                <select
                                    value={filterSeverity}
                                    onChange={(e) => setFilterSeverity(e.target.value)}
                                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                >
                                    <option value="all">All Severities</option>
                                    <option value="CRITICAL">Critical</option>
                                    <option value="HIGH">High</option>
                                    <option value="MEDIUM">Medium</option>
                                    <option value="LOW">Low</option>
                                    <option value="INFORMATIONAL">Informational</option>
                                </select>
                            </div>
                        </div>

                        {/* Row: Assigned To, Framework */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                            {/* Assigned To Filter */}
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Assigned To</label>
                                <select
                                    value={filterAssignedTo}
                                    onChange={(e) => setFilterAssignedTo(e.target.value)}
                                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                >
                                    <option value="all">All</option>
                                    <option value="unassigned">Unassigned</option>
                                    <option value="mine">Assigned to Me</option>
                                    {users
                                        .slice()
                                        .sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''))
                                        .map(u => (
                                            <option key={u.id} value={u.id}>{u.fullName} ({u.email})</option>
                                        ))}
                                </select>
                            </div>

                            {/* Framework Filter */}
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Framework</label>
                                <select
                                    value={filterFramework}
                                    onChange={(e) => setFilterFramework(e.target.value)}
                                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                >
                                    <option value="all">All Frameworks</option>
                                    {uniqueFrameworks.map(framework => (
                                        <option key={framework} value={framework}>{framework}</option>))}
                                </select>
                            </div>
                        </div>

                        {/* Row 3: Deal, Deal Application */}
                        {hasLicense('Deals') && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                            {/* Deal Filter */}
                            {hasLicense('Deals') && (
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Deal</label>
                                <select
                                    value={filterDeal}
                                    onChange={(e) => setFilterDeal(e.target.value)}
                                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                >
                                    <option value="all">All Deals</option>
                                    {allDeals.map((deal) => (
                                        <option key={deal.dealId} value={deal.dealId}>{deal.dealName}</option>))}
                                </select>
                            </div>
                            )}

                            {/* Deal Application Filter */}
                            {hasLicense('Deals') && (
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Deal Application</label>
                                <select
                                    value={filterApplication}
                                    onChange={(e) => setFilterApplication(e.target.value)}
                                    disabled={filterDeal === 'all'}
                                    className={`w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm ${filterDeal === 'all' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                >
                                    <option value="all">{filterDeal === 'all' ? '-- Select a Deal --' : 'All Applications'}</option>
                                    {loadingDealApps
                                        ? <option disabled>Loading...</option>
                                        : allDealApplications.map(app => (
                                            <option key={app.applicationId} value={app.applicationName}>{app.applicationName}</option>
                                        ))
                                    }
                                </select>
                            </div>
                            )}

                        </div>
                        )}

                        {/* Row 4: Business Unit, Business Unit Application */}
                        {hasLicense('BusinessUnits') && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                            {/* Business Unit Filter */}
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Business Unit</label>
                                <select
                                    value={filterBusinessUnit}
                                    onChange={(e) => setFilterBusinessUnit(e.target.value)}
                                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                >
                                    <option value="all">All Business Units</option>
                                    {allBusinessUnits.map((bu) => (
                                        <option key={bu.businessUnitId} value={bu.businessUnitId}>
                                            {bu.businessUnitName}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Business Unit Application Filter */}
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Business Unit Application</label>
                                <select
                                    value={filterBUApplication}
                                    onChange={(e) => setFilterBUApplication(e.target.value)}
                                    disabled={filterBusinessUnit === 'all'}
                                    className={`w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm ${filterBusinessUnit === 'all' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                >
                                    <option value="all">{filterBusinessUnit === 'all' ? '-- Select a Business Unit --' : 'All Applications'}</option>
                                    {uniqueBUApplications.map((app) => (
                                        <option key={app} value={app}>{app}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        )}

                        {/* Row 5: Vendor, All Applications */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                            {hasLicense('TPRM') && (
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Vendor</label>
                                <select
                                    value={filterVendor}
                                    onChange={(e) => setFilterVendor(e.target.value)}
                                    disabled={filterApplication !== 'all' || filterBUApplication !== 'all'}
                                    className={`w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm ${filterApplication !== 'all' || filterBUApplication !== 'all' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                >
                                    <option value="all">All Vendors</option>
                                    {uniqueVendors.map(vendor => (<option key={vendor} value={vendor}>{vendor}</option>))}
                                </select>
                            </div>
                            )}
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">All Applications</label>
                                <select
                                    value={filterAllApplication}
                                    onChange={(e) => setFilterAllApplication(e.target.value)}
                                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                >
                                    <option value="all">All Applications</option>
                                    {uniqueAllApplications.map(app => (<option key={app} value={app}>{app}</option>))}
                                </select>
                            </div>
                        </div>

                        {/* Row: GRC Control Source, GRC Risk Category */}
                        {hasLicense('GRC') && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">GRC Control Source</label>
                                    <select
                                        value={filterControlSource}
                                        onChange={(e) => { setFilterControlSource(e.target.value); setFilterRiskCategory('all'); }}
                                        className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                    >
                                        <option value="all">All Control Sources</option>
                                        {uniqueControlSources.map(s => (<option key={s} value={s}>{s === "RansomwareLiveMonitoring" ? "Ransomware Monitoring" : s}</option>))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">GRC Risk Category</label>
                                    <select
                                        value={filterRiskCategory}
                                        onChange={(e) => setFilterRiskCategory(e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                    >
                                        <option value="all">All Risk Categories</option>
                                        {uniqueRiskCategories.map(c => (<option key={c} value={c}>{c}</option>))}
                                    </select>
                                </div>
                            </div>
                        )}

                        {/* Reset button */}
                        <div className="flex justify-end">
                            <button
                                onClick={resetAllFilters}
                                className="px-4 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md border border-gray-300 transition-colors"
                            >
                                Reset Filters
                            </button>
                        </div>
                    </div>)}
                    </div>

                    {/* Search + Sort bar */}
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                        <div className="relative flex-1 min-w-48">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search risks..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-9 w-full border border-gray-300 rounded-md py-2 text-sm"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 font-medium whitespace-nowrap">Sort by:</span>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                            >
                                <option value="acceptedAt">Acceptance Date</option>
                                <option value="riskTitle">Risk Name</option>
                                <option value="riskSeverity">Risk Severity</option>
                                <option value="applicationName">Application</option>
                                <option value="dealName">Deal</option>
                            </select>
                            <select
                                value={sortOrder}
                                onChange={(e) => setSortOrder(e.target.value)}
                                className="w-28 border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                            >
                                <option value="desc">Descending</option>
                                <option value="asc">Ascending</option>
                            </select>
                        </div>
                    </div>

                    {/* Results */}
                    <div ref={listTopRef} className="mb-6 text-sm text-gray-600">
                        Showing {pagedRisks.length} of {filteredRisks.length} accepted risks
                        {filteredRisks.length !== acceptedRisks.length && ` (filtered from ${acceptedRisks.length} total)`}
                    </div>

                    {filteredRisks.length === 0 ? (
                        <div className="bg-white rounded-lg shadow text-center py-12">
                            <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4"/>
                            <h3 className="text-lg font-medium text-content-primary mb-2">No Accepted Risks</h3>
                            <p className="text-gray-500">
                                {acceptedRisks.length === 0 ? "When risks are accepted during assessments, they will appear here." : "Try adjusting your filters or search terms to find accepted risks."}
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {pagedRisks.map((orgRisk) => (
                                <div key={orgRisk.id} className="bg-white rounded-lg shadow p-6">
                                    {/* Top bar: Title with type and priority pills */}
                                    <div className="flex items-center space-x-3 mb-3">
                                        <h3 className="text-lg font-semibold text-content-primary">{orgRisk.riskTitle}</h3>
                                        <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getTypeColor(orgRisk.riskType)}`}>
                                            {getTypeLabel(orgRisk.riskType)}
                                        </span>
                                        <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getSeverityColor(orgRisk.riskSeverity)}`}>
                                            {formatSeverityLabel(orgRisk.riskSeverity) || orgRisk.riskCategory}
                                        </span>
                                    </div>
                                    <p className="text-sm text-gray-600 mb-3">{getAssessmentDescription(orgRisk)}</p>

                                    {/* Info / Activity Log Chrome tabs */}
                                    <div className="mb-3">
                                        {/* Chrome tab bar */}
                                        <div className="flex items-end gap-0.5 bg-slate-100 border border-gray-200 border-b-0 rounded-t-xl px-2 pt-2">
                                            <button
                                                onClick={() => setCardTabs(prev => ({ ...prev, [orgRisk.id]: 'info' }))}
                                                className={`relative flex items-center gap-2 px-5 py-2.5 text-xs font-semibold rounded-t-lg -mb-px transition-all duration-150 focus:outline-none border ${
                                                    (cardTabs[orgRisk.id] || 'info') === 'info'
                                                        ? 'bg-white text-indigo-700 border-gray-200 z-10 shadow-[0_-1px_3px_rgba(0,0,0,0.06)]'
                                                        : 'bg-transparent border-transparent text-gray-500 hover:bg-white/70 hover:text-gray-700'
                                                }`}
                                                style={(cardTabs[orgRisk.id] || 'info') === 'info' ? { borderBottomColor: '#ffffff' } : {}}
                                            >
                                                Info
                                            </button>
                                            <button
                                                onClick={() => setCardTabs(prev => ({ ...prev, [orgRisk.id]: 'log' }))}
                                                className={`relative flex items-center gap-2 px-5 py-2.5 text-xs font-semibold rounded-t-lg -mb-px transition-all duration-150 focus:outline-none border ${
                                                    (cardTabs[orgRisk.id] || 'info') === 'log'
                                                        ? 'bg-white text-indigo-700 border-gray-200 z-10 shadow-[0_-1px_3px_rgba(0,0,0,0.06)]'
                                                        : 'bg-transparent border-transparent text-gray-500 hover:bg-white/70 hover:text-gray-700'
                                                }`}
                                                style={(cardTabs[orgRisk.id] || 'info') === 'log' ? { borderBottomColor: '#ffffff' } : {}}
                                            >
                                                Activity Log
                                            </button>
                                            <button
                                                onClick={() => setCardTabs(prev => ({ ...prev, [orgRisk.id]: 'emaillog' }))}
                                                className={`relative flex items-center gap-2 px-5 py-2.5 text-xs font-semibold rounded-t-lg -mb-px transition-all duration-150 focus:outline-none border ${
                                                    (cardTabs[orgRisk.id] || 'info') === 'emaillog'
                                                        ? 'bg-white text-indigo-700 border-gray-200 z-10 shadow-[0_-1px_3px_rgba(0,0,0,0.06)]'
                                                        : 'bg-transparent border-transparent text-gray-500 hover:bg-white/70 hover:text-gray-700'
                                                }`}
                                                style={(cardTabs[orgRisk.id] || 'info') === 'emaillog' ? { borderBottomColor: '#ffffff' } : {}}
                                            >
                                                Email Log
                                            </button>
                                            <div className="flex-1 border-b border-gray-200 self-end h-[1px]" />
                                        </div>
                                        {/* Tab content panel */}
                                        <div className="border border-gray-200 border-t-0 rounded-b-xl rounded-tr-xl bg-white p-4 mb-1">
                                            {/* Tab 1: Info */}
                                            {(cardTabs[orgRisk.id] || 'info') === 'info' && (
                                                <>
                                                    {orgRisk.riskDescription && (
                                                        <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                                            <h4 className="font-semibold text-gray-700 mb-1 text-sm">Risk Description</h4>
                                                            <p className="text-gray-700 text-sm">{orgRisk.riskDescription}</p>
                                                        </div>
                                                    )}
                                                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                                                        <h4 className="font-semibold text-blue-900 mb-2 text-sm">Acceptance Details</h4>
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                                            <div>
                                                                <span className="font-medium text-gray-700">Accepted by:</span>
                                                                <span className="ml-1 text-gray-600">{orgRisk.acceptedBy || 'Unknown'}</span>
                                                            </div>
                                                            <div>
                                                                <span className="font-medium text-gray-700">Accepted on:</span>
                                                                <span className="ml-1 text-gray-600">{orgRisk.acceptedAt ? new Date(orgRisk.acceptedAt).toLocaleDateString() : 'Unknown'}</span>
                                                            </div>
                                                            {orgRisk.acceptanceExpiryDate && (
                                                                <div>
                                                                    <span className="font-medium text-gray-700">Acceptance expires:</span>
                                                                    <span className={`ml-1 ${orgRisk.isAcceptanceExpired ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                                                                        {new Date(orgRisk.acceptanceExpiryDate).toLocaleDateString()}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                        {orgRisk.isAcceptanceExpired && (
                                                            <div className="mt-2 flex items-center gap-2 text-sm">
                                                                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800 border border-red-300">
                                                                    Acceptance expired
                                                                </span>
                                                                <span className="text-red-600">Re-review required — reopen or re-accept with a new expiry date.</span>
                                                            </div>
                                                        )}
                                                        {orgRisk.userReason && (
                                                            <div className="mt-2 text-sm">
                                                                <span className="font-medium text-gray-700">Reason:</span>
                                                                <p className="mt-1 text-gray-600">{orgRisk.userReason}</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </>
                                            )}

                                            {/* Tab 2: Activity Log */}
                                            {(cardTabs[orgRisk.id] || 'info') === 'log' && (
                                                <ActivityLogTab
                                                    riskId={orgRisk.id}
                                                    riskType={orgRisk.riskType}
                                                />
                                            )}

                                            {/* Tab 3: Email Log */}
                                            {(cardTabs[orgRisk.id] || 'info') === 'emaillog' && (
                                                <EmailLogTab riskId={String(orgRisk.id)} />
                                            )}
                                        </div>
                                    </div>

                                    {/* Assignment label + action buttons */}
                                    <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                                        {orgRisk.assignedToUserName ? (
                                            <div className="flex items-center gap-1.5 text-sm text-gray-600">
                                                <Shield className="h-3.5 w-3.5 text-indigo-400 shrink-0" />
                                                <span className="font-medium text-gray-500">Risk Assigned:</span>
                                                <span className="font-semibold text-indigo-700">{orgRisk.assignedToUserName}</span>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-1.5 text-sm text-gray-400">
                                                <Shield className="h-3.5 w-3.5 shrink-0" />
                                                <span className="italic">Unassigned</span>
                                            </div>
                                        )}
                                        {canReopenRisk(orgRisk) && (
                                            <button
                                                onClick={() => handleReopenRisk(orgRisk)}
                                                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-orange-700 bg-orange-50 border border-orange-300 rounded-md hover:bg-orange-100 transition-colors"
                                            >
                                                <RotateCcw className="h-4 w-4 mr-1.5"/>
                                                Reopen
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Pagination Controls */}
                    <Pagination
                        page={page}
                        totalPages={totalPages}
                        totalItems={filteredRisks.length}
                        pageSize={pageSize}
                        onPageChange={setPage}
                        onPageSizeChange={setPageSize}
                        scrollTargetRef={listTopRef}
                    />
                </div>
            </div>
        </ErrorBoundary>);
};

export default RiskRegister;
